// logged in user share store.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useToast } from '@/components/ui/toast'
import { logError } from '@/composables/useLogger'
import type {
  ShareLinkCreate,
  ShareLinkResponse,
  ShareDetailsResponse,
  ShareLinkUpdatePayload,
} from '~/types/share'
import { useRuntimeConfig } from '#app'

export const useUserShareStore = defineStore('userShare', () => {
  const userShares = ref<ShareDetailsResponse[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const { toast } = useToast()
  const runtimeConfig = useRuntimeConfig()
  const apiBase = runtimeConfig.public.apiBase

  // --- Internal Helper --- //
  const findShareIndex = (uuid: string): number =>
    userShares.value.findIndex((s) => s.uuid === uuid)

  // --- Actions --- //

  // Action to fetch all share links created by the current user
  // NOTE: Assumes an endpoint like GET /api/v1/share/link exists for the authenticated user.
  // If not, this needs adjustment or removal.
  async function fetchAllUserShares() {
    isLoading.value = true
    error.value = null
    try {
      // Fetch the list of links for the authenticated user
      const response = await $fetch<ShareDetailsResponse[]>(`${apiBase}/share/links`, {
        credentials: 'include',
        method: 'GET',
      })
      userShares.value = response || []
      return userShares.value
    } catch (err) {
      logError('Failed to fetch user share links:', err)
      error.value = 'Failed to load your share links'
      // Don't toast for general list fetch failure unless critical
      // toast({ title: 'Error', description: error.value, variant: 'destructive' })
      return []
    } finally {
      isLoading.value = false
    }
  }

  // Action to fetch details for a specific share link owned by the user
  async function fetchUserShareDetails(shareUuid: string): Promise<ShareDetailsResponse | null> {
    // Doesn't modify store state directly by default, just fetches
    // Could be extended to update userShares array if needed
    try {
      const response = await $fetch<ShareDetailsResponse>(`${apiBase}/share/link/${shareUuid}`, {
        credentials: 'include',
        method: 'GET',
      })
      // Optionally update the state if the item exists or add if new
      const index = findShareIndex(shareUuid)
      if (index !== -1) {
        userShares.value[index] = response
      } else {
        // Decide if fetching details should add it to the main list if not present
        // userShares.value.push(response); // Optional: Add if not found
      }
      return response
    } catch (err) {
      logError(`Failed to fetch user share link details ${shareUuid}:`, err)
      toast({
        title: 'Error',
        description: 'Failed to load share link details',
        variant: 'destructive',
      })
      return null
    }
  }

  // Action to create a new share link
  async function createShareLink(data: ShareLinkCreate): Promise<ShareLinkResponse | null> {
    // This typically returns just the UUID and URL, not the full ShareDetailsResponse
    // So, we don't automatically add it to the userShares list here.
    // A separate call to fetchUserShareDetails or fetchAllUserShares would be needed to update the list.
    isLoading.value = true // Consider specific loading state
    error.value = null
    try {
      const newLink = await $fetch<ShareLinkResponse>(`${apiBase}/share/link`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      toast({
        title: 'Success',
        description: 'Share link created successfully',
      })
      // Consider adding a call to fetchAllUserShares() here if the list should refresh immediately
      return newLink
    } catch (err) {
      logError('Failed to create share link:', err)
      error.value = 'Failed to create share link'
      toast({
        title: 'Error',
        description: error.value,
        variant: 'destructive',
      })
      return null
    } finally {
      isLoading.value = false
    }
  }

  // Action to update an existing share link owned by the user
  async function updateShareLink(
    shareUuid: string,
    payload: ShareLinkUpdatePayload
  ): Promise<ShareDetailsResponse | null> {
    // Consider specific loading state for this operation if needed
    // isLoading.value = true; // Or a more granular loading state
    error.value = null
    try {
      const updatedShare = await $fetch<ShareDetailsResponse>(
        `${apiBase}/share/link/${shareUuid}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      )

      const index = findShareIndex(shareUuid)
      if (index !== -1 && updatedShare) {
        userShares.value[index] = { ...userShares.value[index], ...updatedShare }
      } else if (updatedShare) {
        // If for some reason it wasn't in the list, add it or refresh
        // This case might indicate a need to refresh the whole list or handle differently
        // For now, we'll just update if found.
        // console.warn('Updated share was not found in the local list, consider refreshing.') // Removed console.warn
      }

      toast({
        title: 'Success',
        description: 'Share link settings updated successfully',
      })
      return updatedShare
    } catch (err) {
      logError(`Failed to update share link ${shareUuid}:`, err)
      error.value = 'Failed to update share link settings'
      toast({
        title: 'Error',
        description: error.value,
        variant: 'destructive',
      })
      return null
    } finally {
      // isLoading.value = false;
    }
  }

  // Action to delete a share link owned by the user
  async function deleteShareLink(shareUuid: string): Promise<boolean> {
    isLoading.value = true // Consider specific loading state
    error.value = null
    try {
      await $fetch(`${apiBase}/share/link/${shareUuid}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      const index = findShareIndex(shareUuid)
      if (index !== -1) {
        userShares.value.splice(index, 1)
      }

      toast({
        title: 'Success',
        description: 'Share link deleted successfully',
      })
      return true
    } catch (err) {
      logError(`Failed to delete share link ${shareUuid}:`, err)
      error.value = 'Failed to delete share link'
      toast({
        title: 'Error',
        description: error.value,
        variant: 'destructive',
      })
      return false
    } finally {
      isLoading.value = false
    }
  }

  return {
    userShares,
    isLoading,
    error,
    fetchAllUserShares, // Depends on API endpoint availability
    fetchUserShareDetails,
    createShareLink,
    updateShareLink,
    deleteShareLink,
  }
})
